/**
 * BlogComponents.jsx
 *
 * Reusable content components for blog posts.
 * Enforces consistent structure and improves AI extractability.
 *
 * Components:
 *   - Callout        — Styled box for tips, warnings, stats
 *   - ComparisonTable — Feature comparison with ✓/✗/Limited
 *   - FAQAccordion   — Interactive Q&A with FAQPage-friendly markup
 *   - AtAGlanceTable — Structured summary table
 */

import React, { useState } from 'react';
import { ChevronDown, Info, AlertTriangle, BarChart3, Lightbulb, Calendar, FileText, Link2, CircleHelp, CheckCircle2, Sparkles } from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// CALLOUT
// ═══════════════════════════════════════════════════════════════

const CALLOUT_VARIANTS = {
  info: {
    icon: Info,
    border: 'border-blue-500/20',
    bg: 'bg-blue-500/5',
    iconColor: 'text-blue-400',
    label: 'Note',
  },
  warning: {
    icon: AlertTriangle,
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/5',
    iconColor: 'text-amber-400',
    label: 'Warning',
  },
  stat: {
    icon: BarChart3,
    border: 'border-teal-500/20',
    bg: 'bg-teal-500/5',
    iconColor: 'text-teal-400',
    label: 'Data',
  },
  tip: {
    icon: Lightbulb,
    border: 'border-emerald-500/20',
    bg: 'bg-emerald-500/5',
    iconColor: 'text-emerald-400',
    label: 'Tip',
  },
};

export function Callout({ type = 'info', children }) {
  const variant = CALLOUT_VARIANTS[type] || CALLOUT_VARIANTS.info;
  const Icon = variant.icon;

  return (
    <aside className={`${variant.bg} ${variant.border} border rounded-xl p-4 my-6`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={`w-4 h-4 ${variant.iconColor}`} />
        </div>
        <div className="text-sm text-zinc-300 leading-relaxed">
          <span className={`text-xs font-semibold uppercase tracking-wider ${variant.iconColor} mr-2`}>
            {variant.label}
          </span>
          {children}
        </div>
      </div>
    </aside>
  );
}

export function ShortAnswerBlock({ children, label = 'Short answer' }) {
  return (
    <aside className="bg-teal-500/5 border border-teal-500/20 rounded-xl p-4 my-4">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="w-4 h-4 text-teal-400" />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-teal-400">
          {label}
        </span>
      </div>
      <div className="text-sm text-zinc-300 leading-relaxed">
        {children}
      </div>
    </aside>
  );
}

/**
 * Detect callout syntax in a blockquote's text content.
 * Supports: > [!tip], > [!warning], > [!stat], > [!info]
 * Returns { type, content } or null if not a callout.
 */
export function parseCalloutFromBlockquote(children) {
  const text = extractTextFromChildren(children);
  const match = text.match(/^\[!(tip|warning|stat|info)\]\s*([\s\S]*)$/i);
  if (!match) return null;
  return { type: match[1].toLowerCase(), content: match[2].trim() };
}

function extractTextFromChildren(children) {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractTextFromChildren).join('');
  if (children?.props?.children) return extractTextFromChildren(children.props.children);
  return '';
}

// ═══════════════════════════════════════════════════════════════
// COMPARISON TABLE
// ═══════════════════════════════════════════════════════════════

function CellValue({ value }) {
  if (value === true) return <span className="text-emerald-400 font-medium">✓</span>;
  if (value === false) return <span className="text-zinc-600">✗</span>;
  if (typeof value === 'string' && value.toLowerCase() === 'limited')
    return <span className="text-amber-400 text-xs font-medium">Limited</span>;
  return <span className="text-zinc-300 text-sm">{String(value)}</span>;
}

export function ComparisonTable({ tools = [], features = [], verifiedDate }) {
  if (!features.length || !tools.length) return null;

  return (
    <div id="comparison-table" className="my-8">
      <div className="bg-white/[0.02] border border-white/[0.08] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="bg-white/[0.04]">
              <tr>
                <th className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider border-b border-white/[0.08]">
                  Feature
                </th>
                {tools.map(tool => (
                  <th key={tool} className="px-5 py-3.5 text-left text-xs font-semibold text-zinc-300 uppercase tracking-wider border-b border-white/[0.08]">
                    {tool}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {features.map((feature, i) => (
                <tr key={feature.name} className={i % 2 === 1 ? 'bg-white/[0.02]' : ''}>
                  <td className="px-5 py-3 text-sm text-zinc-200 font-medium border-b border-white/[0.05]">
                    {feature.name}
                  </td>
                  {tools.map(tool => (
                    <td key={tool} className="px-5 py-3 border-b border-white/[0.05] text-center">
                      <CellValue value={feature[tool]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {verifiedDate && (
          <div className="px-5 py-2.5 border-t border-white/[0.05] text-[11px] text-zinc-600">
            Feature data as of {verifiedDate}. Verify with vendors before purchasing.
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FAQ ACCORDION
// ═══════════════════════════════════════════════════════════════

function FAQItem({ question, answer, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden cursor-pointer hover:border-white/[0.10] transition-colors"
      onClick={() => setIsOpen(!isOpen)}
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
    >
      <div className="flex items-center justify-between p-4 sm:p-5">
        <h3 className="text-white font-medium pr-4 text-sm sm:text-base" itemProp="name">
          {question}
        </h3>
        <ChevronDown
          className={`w-5 h-5 text-zinc-400 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </div>
      <div
        className={`overflow-hidden transition-all duration-300 ease-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
        itemScope
        itemProp="acceptedAnswer"
        itemType="https://schema.org/Answer"
      >
        <p
          className="px-4 sm:px-5 pb-4 sm:pb-5 text-xs sm:text-sm text-zinc-400 leading-relaxed border-t border-white/5 pt-3 sm:pt-4"
          itemProp="text"
        >
          {answer}
        </p>
      </div>
    </div>
  );
}

export function FAQAccordion({ items = [], title = 'Frequently Asked Questions' }) {
  if (!items.length) return null;

  return (
    <section id="faq" className="my-10" itemScope itemType="https://schema.org/FAQPage">
      <h2 className="text-xl font-medium text-white mt-8 mb-5 pb-2 border-b border-zinc-800">
        {title}
      </h2>
      <div className="space-y-3">
        {items.map((item, i) => (
          <FAQItem
            key={item.question}
            question={item.question}
            answer={item.answer}
            defaultOpen={i === 0}
          />
        ))}
      </div>
    </section>
  );
}

export function EvidencePanel({
  lastVerified,
  reviewLabel = 'Last reviewed',
  sourceTypes = [],
  pricingSource,
  docsChecked = [],
  knownUnknowns = [],
  className = '',
}) {
  const sourceList = sourceTypes.filter(Boolean);
  const docsList = docsChecked.filter(Boolean);
  const unknownList = knownUnknowns.filter(Boolean);

  return (
    <section className={`bg-white/[0.02] border border-white/[0.08] rounded-xl p-5 my-8 ${className}`}>
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">Evidence Panel</h2>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <PanelRow
          icon={Calendar}
          label={reviewLabel}
          value={lastVerified || 'Not specified'}
        />
        <PanelRow
          icon={Link2}
          label="Pricing source"
          value={pricingSource || 'Referenced in page content and vendor materials where available'}
        />
        <PanelList
          icon={FileText}
          label="Source types used"
          items={sourceList.length ? sourceList : ['Official documentation', 'Touchstone research notes']}
        />
        <PanelList
          icon={Info}
          label="Docs checked"
          items={docsList.length ? docsList : ['Primary product documentation']}
        />
        <div className="md:col-span-2">
          <PanelList
            icon={CircleHelp}
            label="Known unknowns"
            items={unknownList.length ? unknownList : ['Capabilities, pricing, and limits can change between review cycles.']}
          />
        </div>
      </div>
    </section>
  );
}

export function FreshnessTimeline({ title = 'What changed recently', items = [] }) {
  const visibleItems = items.filter((item) => item?.label && item?.detail);
  if (!visibleItems.length) return null;

  return (
    <section className="bg-white/[0.02] border border-white/[0.08] rounded-xl p-5 my-8">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-4 h-4 text-blue-400" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">{title}</h2>
      </div>
      <div className="space-y-4">
        {visibleItems.map((item, index) => (
          <div key={`${item.label}-${index}`} className="flex gap-3">
            <div className="flex flex-col items-center pt-1">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-400" />
              {index < visibleItems.length - 1 && <div className="w-px h-full bg-white/[0.08] mt-2" />}
            </div>
            <div className="pb-2">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">{item.label}</div>
              <div className="text-sm text-white">{item.detail}</div>
              {item.note && <div className="text-xs text-zinc-500 mt-1">{item.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════
// AT A GLANCE TABLE
// ═══════════════════════════════════════════════════════════════

export function AtAGlanceTable({ rows = [] }) {
  if (!rows.length) return null;

  return (
    <div id="at-a-glance" className="my-8">
      <div className="bg-white/[0.02] border border-white/[0.08] rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-white/[0.06]">
          <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider">At a Glance</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="bg-white/[0.04]">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider border-b border-white/[0.08]">
                  Attribute
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider border-b border-white/[0.08]">
                  Detail
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ label, value }, i) => (
                <tr key={label} className={i % 2 === 1 ? 'bg-white/[0.02]' : ''}>
                  <td className="px-5 py-3 text-sm text-zinc-200 font-medium whitespace-nowrap border-b border-white/[0.05]">
                    {label}
                  </td>
                  <td className="px-5 py-3 text-sm text-zinc-400 border-b border-white/[0.05]">
                    {value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PanelRow({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 mb-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <div className="text-sm text-zinc-200 leading-relaxed break-words">{value}</div>
    </div>
  );
}

function PanelList({ icon: Icon, label, items }) {
  return (
    <div className="bg-white/[0.02] border border-white/[0.05] rounded-lg p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-zinc-500 mb-2">
        <Icon className="w-3.5 h-3.5" />
        {label}
      </div>
      <ul className="space-y-1.5">
        {items.map((item, index) => (
          <li key={`${label}-${index}`} className="text-sm text-zinc-200 leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
